<template>
  <div class="sub-wizard">
    <div class="container py-4">

      <!-- Success screen -->
      <div v-if="done" class="sub-success">
        <div class="sub-success__check"><i class="pi pi-check"></i></div>
        <h2>تم إرسال طلب الدفع بنجاح</h2>
        <p>سيقوم فريقنا بمراجعة الإيصال وتفعيل اشتراكك في أقرب وقت. ستصلك إشعار عند الموافقة.</p>
        <div class="sub-success__actions">
          <router-link v-if="returnUrl" :to="returnUrl" class="btn-primary-grad"><i class="pi pi-arrow-right"></i> العودة للمحتوى</router-link>
          <router-link to="/my-subscriptions" class="btn-primary-grad"><i class="pi pi-id-card"></i> اشتراكاتي</router-link>
          <router-link to="/payment-history" class="btn-ghost">سجل المدفوعات</router-link>
        </div>
      </div>

      <template v-else>
        <!-- Header -->
        <div class="sub-head">
          <button class="sub-back" @click="goBack" aria-label="رجوع"><i class="pi pi-arrow-right"></i></button>
          <div>
            <h1 class="sub-title">إتمام الاشتراك</h1>
            <p class="sub-sub" v-if="productName">{{ productName }}</p>
          </div>
        </div>

        <!-- Stepper -->
        <div class="sub-steps">
          <div v-for="(label, i) in stepLabels" :key="i" class="sub-step"
               :class="{ active: step === i + 1, done: step > i + 1 }">
            <span class="sub-step__dot">{{ step > i + 1 ? '✓' : i + 1 }}</span>
            <span class="sub-step__label">{{ label }}</span>
          </div>
        </div>

        <!-- Loading initial -->
        <div v-if="loading" class="sub-loading"><i class="pi pi-spin pi-spinner"></i></div>

        <div v-else class="sub-panel">
          <!-- STEP 1: plan -->
          <template v-if="step === 1">
            <h3 class="sub-h3">اختر الخطة</h3>
            <div v-if="plans.length" class="sub-plans">
              <button v-for="p in plans" :key="p.type" class="sub-plan" :class="{ active: form.plan === p.type }" @click="form.plan = p.type">
                <span class="sub-plan__name">{{ p.label }}</span>
                <span class="sub-plan__price">{{ money(p.price) }}</span>
              </button>
            </div>
            <p v-else class="sub-empty">لا توجد خطط متاحة لهذا المنتج.</p>
            <button class="sub-next" :disabled="!form.plan || busy" @click="createAndNext">
              <span v-if="busy"><i class="pi pi-spin pi-spinner"></i></span>
              <span v-else>التالي <i class="pi pi-arrow-left"></i></span>
            </button>
          </template>

          <!-- STEP 2: payment method -->
          <template v-else-if="step === 2">
            <h3 class="sub-h3">اختر طريقة الدفع</h3>
            <div v-if="methods.length" class="sub-methods">
              <button v-for="m in methods" :key="m.id" class="sub-method" :class="{ active: form.method && form.method.id === m.id }" @click="form.method = m">
                <img v-if="m.image_link" :src="m.image_link" :alt="m.name" />
                <span v-else class="sub-method__icon"><i class="pi pi-wallet"></i></span>
                <span class="sub-method__name">{{ m.name }}</span>
                <i v-if="form.method && form.method.id === m.id" class="pi pi-check-circle sub-method__check"></i>
              </button>
            </div>
            <p v-else class="sub-empty">لا توجد طرق دفع متاحة حالياً.</p>
            <div class="sub-nav">
              <button class="sub-prev" @click="step = 1">السابق</button>
              <button class="sub-next inline" :disabled="!form.method" @click="step = 3">التالي <i class="pi pi-arrow-left"></i></button>
            </div>
          </template>

          <!-- STEP 3: payment details -->
          <template v-else-if="step === 3">
            <h3 class="sub-h3">بيانات الدفع</h3>
            <div class="sub-paydetails" v-if="form.method">
              <div class="sub-amount">المبلغ المطلوب: <strong>{{ money(planPrice) }}</strong></div>
              <div v-if="form.method.account_name" class="sub-info-row"><span>اسم الحساب</span><b>{{ form.method.account_name }}</b><button class="copy" @click="copy(form.method.account_name)"><i class="pi pi-copy"></i></button></div>
              <div v-if="form.method.account_number" class="sub-info-row"><span>رقم الحساب</span><b>{{ form.method.account_number }}</b><button class="copy" @click="copy(form.method.account_number)"><i class="pi pi-copy"></i></button></div>
              <div v-if="form.method.iban" class="sub-info-row"><span>IBAN</span><b>{{ form.method.iban }}</b><button class="copy" @click="copy(form.method.iban)"><i class="pi pi-copy"></i></button></div>
              <div v-if="form.method.qr_image_link" class="sub-qr">
                <img :src="form.method.qr_image_link" alt="QR" @click="previewImage(form.method.qr_image_link)" />
                <small>اضغط لتكبير رمز QR</small>
              </div>
              <div v-if="form.method.instructions" class="sub-instructions">
                <i class="pi pi-info-circle"></i> {{ form.method.instructions }}
              </div>
            </div>
            <div class="sub-nav">
              <button class="sub-prev" @click="step = 2">السابق</button>
              <button class="sub-next inline" @click="step = 4">التالي <i class="pi pi-arrow-left"></i></button>
            </div>
          </template>

          <!-- STEP 4: upload receipt + transaction + submit -->
          <template v-else-if="step === 4">
            <h3 class="sub-h3">رفع الإيصال وإتمام الطلب</h3>

            <label class="sub-upload" :class="{ 'has-file': preview }">
              <input type="file" accept="image/*" capture="environment" @change="onReceipt" hidden />
              <template v-if="preview">
                <img :src="preview" alt="الإيصال" class="sub-upload__preview" />
                <span class="sub-upload__change"><i class="pi pi-refresh"></i> تغيير الصورة</span>
              </template>
              <template v-else>
                <i class="pi pi-cloud-upload"></i>
                <span>اضغط لرفع صورة الإيصال أو التقاطها بالكاميرا</span>
              </template>
            </label>

            <div class="sub-field">
              <label>رقم العملية</label>
              <input type="text" v-model="form.transaction_number" placeholder="أدخل رقم العملية من الإيصال" />
            </div>

            <div class="sub-field">
              <label>ملاحظات (اختياري)</label>
              <textarea v-model="form.notes" rows="2" placeholder="أي ملاحظات إضافية..."></textarea>
            </div>

            <div class="sub-nav">
              <button class="sub-prev" @click="step = 3">السابق</button>
              <button class="sub-next inline" :disabled="!receiptFile || submitting" @click="submit">
                <span v-if="submitting"><i class="pi pi-spin pi-spinner"></i> جارٍ الإرسال</span>
                <span v-else><i class="pi pi-check"></i> إرسال الطلب</span>
              </button>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans, formatMoney, apiError, normalizeSubscribableType } from '@/composables/subscriptionMeta.js';

export default {
  name: 'SubscribeWizard',
  data() {
    return {
      step: 1,
      stepLabels: ['الخطة', 'طريقة الدفع', 'بيانات الدفع', 'الإيصال'],
      loading: true,
      busy: false,
      submitting: false,
      done: false,
      product: null,
      productName: '',
      plans: [],
      methods: [],
      subscriptionId: null,
      receiptFile: null,
      preview: null,
      form: { plan: null, method: null, transaction_number: '', notes: '' },
    };
  },
  computed: {
    // Tolerates every academic-year spelling seen in the wild (see
    // normalizeSubscribableType) so an academic year is never mis-routed
    // through the Course API (the "academicyear" vs "academic_year" mismatch bug).
    type() { return normalizeSubscribableType(this.$route.query.type); },
    id() { return this.$route.query.id; },
    returnUrl() { return this.$route.query.return || null; },
    planPrice() {
      const p = this.plans.find(x => x.type === this.form.plan);
      return p ? p.price : null;
    },
  },
  methods: {
    money: formatMoney,
    async loadProduct() {
      this.loading = true;
      try {
        if (this.type === 'course') {
          this.product = await subscriptionService.course(this.id);
          this.productName = this.product?.title || '';
        } else {
          const years = await subscriptionService.academicYears();
          this.product = years.find(y => String(y.id) === String(this.id) || y.slug === this.id) || null;
          this.productName = this.product?.name || '';
        }
        this.plans = productPlans(this.product);
        // preselect plan from query or first
        const wanted = this.$route.query.plan;
        this.form.plan = this.plans.find(p => p.type === wanted)?.type || this.plans[0]?.type || null;
        // preload active payment methods
        this.methods = await subscriptionService.paymentMethods();
      } catch {
        this.product = null;
      } finally {
        this.loading = false;
      }
    },
    async createAndNext() {
      if (!this.form.plan) return;
      this.busy = true;
      try {
        const data = await subscriptionService.subscribe({
          type: this.type, subscribable_id: this.id, plan_type: this.form.plan,
        });
        this.subscriptionId = data.subscription.id;
        if (Array.isArray(data.payment_methods) && data.payment_methods.length) {
          this.methods = data.payment_methods;
        }
        this.step = 2;
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      } finally {
        this.busy = false;
      }
    },
    onReceipt(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.receiptFile = file;
      this.preview = URL.createObjectURL(file);
    },
    async submit() {
      if (!this.receiptFile) {
        Swal.fire({ icon: 'warning', title: 'برجاء رفع صورة الإيصال', timer: 2000, showConfirmButton: false });
        return;
      }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('subscription_id', this.subscriptionId);
        fd.append('payment_method_id', this.form.method.id);
        if (this.planPrice != null) fd.append('amount', this.planPrice);
        if (this.form.transaction_number) fd.append('transaction_number', this.form.transaction_number);
        if (this.form.notes) fd.append('notes', this.form.notes);
        fd.append('receipt_image', this.receiptFile);
        await subscriptionService.uploadReceipt(fd);
        this.done = true;
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 3000, showConfirmButton: false });
      } finally {
        this.submitting = false;
      }
    },
    copy(text) {
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(fallback);
      } else { fallback(); }
      Swal.fire({ icon: 'success', title: 'تم النسخ', timer: 900, showConfirmButton: false, toast: true, position: 'top' });
    },
    previewImage(src) {
      Swal.fire({ imageUrl: src, imageAlt: 'QR', showConfirmButton: false, showCloseButton: true, width: 'auto' });
    },
    goBack() {
      if (this.step > 1) { this.step -= 1; return; }
      this.$router.back();
    },
  },
  async mounted() {
    if (!this.id) { this.$router.replace('/courses'); return; }
    await this.loadProduct();
  },
};
</script>

<style scoped>
.sub-wizard { min-height: 70vh; padding-bottom: 5rem; }
.sub-head { display: flex; align-items: center; gap: .8rem; margin-bottom: 1.5rem; }
.sub-back { background: #f1f5f9; border: none; width: 40px; height: 40px; border-radius: 12px; cursor: pointer; color: #374151; }
.sub-title { font-size: 1.4rem; font-weight: 800; margin: 0; color: #111827; }
.sub-sub { margin: 0; color: #6B7280; font-size: .9rem; }
.sub-steps { display: flex; justify-content: space-between; margin-bottom: 1.75rem; gap: .25rem; }
.sub-step { display: flex; flex-direction: column; align-items: center; gap: .35rem; flex: 1; position: relative; }
.sub-step__dot {
  width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: #E5E7EB; color: #94a3b8; font-weight: 800; font-size: .85rem; z-index: 1;
}
.sub-step.active .sub-step__dot { background: #051629; color: #fff; }
.sub-step.done .sub-step__dot { background: #22c55e; color: #fff; }
.sub-step__label { font-size: .72rem; color: #94a3b8; font-weight: 700; }
.sub-step.active .sub-step__label { color: #051629; }
.sub-panel { background: #fff; border: 1px solid rgba(5, 22, 41,.1); border-radius: 18px; padding: 1.4rem; }
.sub-h3 { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: 1rem; }
.sub-loading { text-align: center; padding: 3rem; color: #051629; font-size: 1.6rem; }
.sub-plans, .sub-methods { display: grid; gap: .6rem; margin-bottom: 1.2rem; }
.sub-plan { display: flex; justify-content: space-between; align-items: center; border: 1.5px solid #E5E7EB; background: #fff; border-radius: 12px; padding: .85rem 1.1rem; cursor: pointer; transition: all .15s; }
.sub-plan.active { border-color: #051629; background: rgba(5, 22, 41,.05); }
.sub-plan__name { font-weight: 700; color: #374151; }
.sub-plan__price { font-weight: 800; color: #051629; }
.sub-method { display: flex; align-items: center; gap: .8rem; border: 1.5px solid #E5E7EB; background: #fff; border-radius: 12px; padding: .7rem 1rem; cursor: pointer; transition: all .15s; }
.sub-method.active { border-color: #051629; background: rgba(5, 22, 41,.05); }
.sub-method img { width: 40px; height: 40px; object-fit: contain; border-radius: 8px; }
.sub-method__icon { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: #f1f5f9; border-radius: 8px; color: #051629; }
.sub-method__name { font-weight: 700; color: #374151; flex: 1; text-align: start; }
.sub-method__check { color: #051629; }
.sub-empty { color: #94a3b8; text-align: center; padding: 1.5rem; }
.sub-amount { background: rgba(5, 22, 41,.06); border-radius: 12px; padding: .8rem 1rem; margin-bottom: 1rem; text-align: center; color: #374151; }
.sub-amount strong { color: #051629; font-size: 1.15rem; }
.sub-info-row { display: flex; align-items: center; gap: .5rem; padding: .7rem 0; border-bottom: 1px solid #f1f5f9; }
.sub-info-row span { color: #6B7280; font-size: .85rem; min-width: 90px; }
.sub-info-row b { flex: 1; color: #111827; word-break: break-all; }
.copy { background: none; border: none; color: #051629; cursor: pointer; }
.sub-qr { text-align: center; margin: 1rem 0; }
.sub-qr img { width: 150px; height: 150px; object-fit: contain; border: 1px solid #E5E7EB; border-radius: 12px; cursor: pointer; }
.sub-qr small { display: block; color: #94a3b8; margin-top: .3rem; }
.sub-instructions { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 12px; padding: .8rem 1rem; margin-top: 1rem; font-size: .85rem; line-height: 1.7; }
.sub-upload {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .5rem;
  border: 2px dashed #c7d2fe; border-radius: 16px; padding: 1.5rem; cursor: pointer;
  color: #6B7280; text-align: center; margin-bottom: 1.2rem; transition: border-color .2s;
}
.sub-upload:hover { border-color: #051629; }
.sub-upload .pi-cloud-upload { font-size: 2rem; color: #051629; }
.sub-upload__preview { max-height: 220px; border-radius: 12px; }
.sub-upload__change { color: #051629; font-weight: 700; font-size: .85rem; }
.sub-field { margin-bottom: 1rem; }
.sub-field label { display: block; font-weight: 700; color: #374151; margin-bottom: .4rem; font-size: .9rem; }
.sub-field input, .sub-field textarea { width: 100%; border: 1px solid #E5E7EB; border-radius: 12px; padding: .7rem 1rem; outline: none; }
.sub-field input:focus, .sub-field textarea:focus { border-color: #051629; }
.sub-next {
  width: 100%; background: linear-gradient(90deg, #051629, #f7b424); color: #fff;
  border: none; border-radius: 12px; padding: .8rem; font-weight: 800; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
}
.sub-next.inline { width: auto; flex: 1; }
.sub-next:disabled { background: #cbd5e1; cursor: not-allowed; }
.sub-nav { display: flex; gap: .6rem; align-items: center; }
.sub-prev { background: #f1f5f9; border: none; border-radius: 12px; padding: .8rem 1.2rem; font-weight: 700; color: #374151; cursor: pointer; }
.sub-success { text-align: center; padding: 3rem 1rem; max-width: 520px; margin: 0 auto; }
.sub-success__check {
  width: 84px; height: 84px; border-radius: 50%; background: #22c55e; color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: 2.4rem; margin: 0 auto 1.4rem;
  animation: pop .4s ease;
}
@keyframes pop { 0% { transform: scale(0); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
.sub-success h2 { font-weight: 800; color: #111827; }
.sub-success p { color: #6B7280; line-height: 1.8; }
.sub-success__actions { display: flex; flex-direction: column; gap: .6rem; max-width: 320px; margin: 1.5rem auto 0; }
.btn-primary-grad {
  background: linear-gradient(90deg, #051629, #f7b424); color: #fff; text-decoration: none;
  border-radius: 12px; padding: .75rem 1rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
}
.btn-ghost { color: #051629; text-decoration: none; font-weight: 700; padding: .5rem; }
</style>
